import { ReactElement, useEffect, useMemo, useState } from 'react';
import { HiOutlineCheckCircle, HiOutlinePencil } from 'react-icons/hi2';
import Button from '../../../../../components/Button';
import {
  ResolvedWeather,
  WeatherChannelKey,
  WeatherControllerDict,
  WeatherSourceKey,
} from '../../../../../types/Session';
import { ATMOSPHERIC_SOURCE_LABEL, AtmosphericSourceEnum } from '../../../../../types/Run';

interface SourceRowConfig {
  name: string;
  key: WeatherSourceKey;
  dotColor: string;
  editable: boolean;
}

const SOURCE_ROWS: SourceRowConfig[] = [
  { name: 'Weather Api', key: 'weather_api', dotColor: '#3b82f6', editable: true },
  { name: 'Probe', key: 'probe', dotColor: '#10b981', editable: true },
  { name: 'Probe-API', key: 'probe_hybrid', dotColor: '#06b6d4', editable: false },
  { name: 'Local Weather Station', key: 'local_station', dotColor: '#f59e0b', editable: true },
  { name: 'Manual', key: 'manual', dotColor: '#8b5cf6', editable: true },
  { name: 'Bike Computer', key: 'bike_computer', dotColor: '#4b5563', editable: false },
];

const CHANNELS: { key: WeatherChannelKey; label: string }[] = [
  { key: 'Temp_T', label: 'Temp' },
  { key: 'Baro_P', label: 'Baro' },
  { key: 'Humidity_x', label: 'Humidity' },
];

type ChannelSelection = Partial<Record<WeatherChannelKey, WeatherSourceKey>>;

interface SourceChannelMatrixProps {
  wcd: WeatherControllerDict | null;
  resolvedWeather: ResolvedWeather | null | undefined;
  saving: boolean;
  onSaveChannelSelection: (channelSelection: ChannelSelection) => Promise<void>;
  showBikeComputer: boolean;
  expandedWeatherEdit: string | null;
  onToggleEdit: (name: string) => void;
}

const meanOfChannel = (wcd: WeatherControllerDict | null, source: WeatherSourceKey,
                       channel: WeatherChannelKey): number | null => {
  const values = (wcd?.[source]?.weather_data ?? [])
    .map((d) => d[channel])
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (values.length === 0) {
    return null;
  }
  return values.reduce((a, b) => a + b, 0) / values.length;
};

const formatChannelValue = (channel: WeatherChannelKey, value: number | null): string => {
  if (value === null) {
    return '—';
  }
  if (channel === 'Temp_T') {
    return `${value.toFixed(1)}°`;
  }
  if (channel === 'Baro_P') {
    return `${(value / 100).toFixed(0)} hPa`;
  }
  return `${value.toFixed(0)}%`;
};

const sourceLabel = (value: number): string =>
  ATMOSPHERIC_SOURCE_LABEL[value as AtmosphericSourceEnum] ?? String(value);

/**
 * Per-channel source selection matrix. Rows are the weather sources, columns
 * the atmospheric channels (Temp / Baro / Humidity), one radio per column.
 * A channel with no explicit override follows the session-wide selection —
 * except Baro, whose default is the probe-anchored ladder (sensor anchors,
 * API-slope extrapolation, std atmosphere), shown as the Probe row.
 */
export default function SourceChannelMatrix({
  wcd,
  resolvedWeather,
  saving,
  onSaveChannelSelection,
  showBikeComputer,
  expandedWeatherEdit,
  onToggleEdit,
}: SourceChannelMatrixProps): ReactElement {
  const stored: ChannelSelection = useMemo(() => wcd?.channel_selection ?? {}, [wcd]);
  const [draft, setDraft] = useState<ChannelSelection>(stored);

  useEffect(() => {
    setDraft(stored);
  }, [stored]);

  const effectiveFor = (selection: ChannelSelection, channel: WeatherChannelKey): WeatherSourceKey => {
    const explicit = selection[channel];
    if (explicit) {
      return explicit;
    }
    // Baro without an explicit override runs the probe-anchored ladder.
    if (channel === 'Baro_P') {
      return 'probe';
    }
    return wcd?.selection ?? 'probe_hybrid';
  };

  const dirty = CHANNELS.some(({ key }) => effectiveFor(draft, key) !== effectiveFor(stored, key));

  const handlePick = (channel: WeatherChannelKey, source: WeatherSourceKey) => {
    setDraft((prev) => ({ ...prev, [channel]: source }));
  };

  const densitySample = useMemo(() => {
    const samples = resolvedWeather?.samples ?? [];
    if (samples.length === 0) {
      return null;
    }
    return samples[Math.floor(samples.length / 2)];
  }, [resolvedWeather]);

  const rows = SOURCE_ROWS.filter((row) => row.key !== 'bike_computer' || showBikeComputer);

  return (
    <div className="cr-flex cr-flex-col cr-gap-3">
      <div className="cr-overflow-x-auto">
        <table className="cr-min-w-full cr-text-sm">
          <thead>
            <tr className="cr-text-left cr-text-xs cr-font-semibold cr-uppercase cr-text-gray-500">
              <th className="cr-py-2 cr-pr-4">Source</th>
              {CHANNELS.map((ch) => (
                <th key={ch.key} className="cr-px-2 cr-py-2 cr-text-center">{ch.label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="cr-divide-y cr-divide-gray-200">
            {rows.map((row) => (
              <tr key={row.key} className="cr-text-gray-700">
                <td className="cr-py-2 cr-pr-4">
                  <div className="cr-flex cr-items-center">
                    <span
                      className="cr-mr-2 cr-inline-block cr-h-3 cr-w-3 cr-flex-shrink-0 cr-rounded-full"
                      style={{ backgroundColor: row.dotColor }}
                    />
                    <span className="cr-whitespace-nowrap">
                      {row.name}
                      {row.key === 'probe' && (
                        <span className="cr-ml-1 cr-text-xs cr-text-gray-400">(anchored)</span>
                      )}
                    </span>
                    {/* Kept beside the label, not in a trailing column: the matrix
                        scrolls horizontally in the narrow panel and an action
                        column at the far right gets clipped out of view. */}
                    {row.editable && (
                      <button
                        type="button"
                        onClick={() => onToggleEdit(row.name)}
                        className={`cr-ml-2 cr-flex-shrink-0 hover:cr-text-gray-900 ${
                          expandedWeatherEdit === row.name ? 'cr-text-blue-600' : 'cr-text-gray-400'
                        }`}
                        title={`Edit ${row.name}`}
                      >
                        <HiOutlinePencil className="cr-h-4 cr-w-4" />
                      </button>
                    )}
                  </div>
                </td>
                {CHANNELS.map((ch) => {
                  const value = meanOfChannel(wcd, row.key, ch.key);
                  // The probe row stays selectable for Baro even without
                  // stored probe pressure — it names the ladder, which
                  // falls through to the API / std atmosphere on its own.
                  const selectable = value !== null || (ch.key === 'Baro_P' && row.key === 'probe');
                  const checked = effectiveFor(draft, ch.key) === row.key;
                  return (
                    <td key={ch.key} className="cr-px-2 cr-py-2 cr-text-center">
                      <label className="cr-flex cr-flex-col cr-items-center cr-gap-1">
                        <input
                          type="radio"
                          name={`met-src-${ch.key}`}
                          value={row.key}
                          checked={checked}
                          disabled={!selectable || saving}
                          onChange={() => handlePick(ch.key, row.key)}
                          className="cr-text-cirrusBlue-500 focus:cr-ring-cirrusBlue-500 disabled:cr-opacity-30"
                        />
                        <span className="cr-text-xs cr-text-gray-400">
                          {formatChannelValue(ch.key, value)}
                        </span>
                      </label>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Read-only computed air density (server-resolved, mid-session sample) */}
      <div className="cr-rounded cr-bg-neutral-100 cr-p-3">
        {densitySample ? (
          <div className="cr-flex cr-flex-wrap cr-items-baseline cr-gap-x-3 cr-gap-y-1">
            <span className="cr-text-sm cr-font-medium cr-text-blue-900">
              Air density: {densitySample.AirDensity_d.toFixed(4)} kg/m³
            </span>
            <span className="cr-text-xs cr-text-gray-500">
              T: {sourceLabel(densitySample.sources.temperature)}
              {' · '}P: {sourceLabel(densitySample.sources.barometric_pressure)}
              {' · '}RH: {sourceLabel(densitySample.sources.humidity)}
            </span>
          </div>
        ) : (
          <span className="cr-text-sm cr-text-gray-500">Air density: not yet resolved</span>
        )}
      </div>

      {dirty && (
        <div className="cr-flex cr-justify-end">
          <Button
            onClick={() => onSaveChannelSelection(draft)}
            disabled={saving}
            icon={<HiOutlineCheckCircle className="cr-h-5 cr-w-5 cr-stroke-[2.2px]" />}
            normalCase
            className="cr-font-semibold"
          >
            {saving ? 'Saving...' : 'Save Sources'}
          </Button>
        </div>
      )}
    </div>
  );
}
